import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { DEFAULT_LIST_PAGE_SIZE, PRODUCTS_API_URL } from '../../constants';
import { paginateRows } from '../../domain/contracts';
import { ProductsGrid, type ProductRow } from '../grids/ProductsGrid';
import { ProductTiles, type ProductFilter } from '../summary/ProductTiles';
import { ProductDetailSidePanel } from './ProductDetailSidePanel';

type AuthFetch = (url: string, init?: RequestInit) => Promise<Response>;

export function ProductsRegister({ authFetch }: { authFetch: AuthFetch }): ReactElement {
  const [rows, setRows] = useState<ProductRow[]>([]);
  const [filter, setFilter] = useState<ProductFilter>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_LIST_PAGE_SIZE);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await authFetch(PRODUCTS_API_URL);
      if (!res.ok) {
        setError((await res.text()) || 'Could not load products');
        return;
      }
      const body = (await res.json()) as { items?: ProductRow[] };
      setRows(body.items ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load products');
    }
  }, [authFetch]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    if (filter === 'crude') {
      return rows.filter((r) => r.class.toLowerCase() === 'crude');
    }
    if (filter === 'gas') {
      return rows.filter((r) => r.class.toLowerCase() === 'gas');
    }
    if (filter === 'inactive') {
      return rows.filter((r) => !r.active);
    }
    return rows;
  }, [filter, rows]);

  const counts = useMemo(
    () => ({
      all: rows.length,
      crude: rows.filter((r) => r.class.toLowerCase() === 'crude').length,
      gas: rows.filter((r) => r.class.toLowerCase() === 'gas').length,
      inactive: rows.filter((r) => !r.active).length
    }),
    [rows]
  );

  const pageRows = useMemo(() => paginateRows(filtered, page, pageSize), [filtered, page, pageSize]);
  const selected = rows.find((r) => r.id === selectedId) ?? null;

  return (
    <>
      {error && <p className="error">{error}</p>}
      <ProductTiles
        counts={counts}
        filter={filter}
        onFilter={(next) => {
          setFilter(next);
          setPage(1);
          setSelectedId(null);
        }}
      />
      <div className={`requests-shell${selectedId ? ' has-panel' : ''}`}>
        <ProductsGrid
          rows={pageRows}
          selectedId={selectedId}
          onSelect={setSelectedId}
          page={page}
          pageSize={pageSize}
          totalCount={filtered.length}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
        {selected && (
          <ProductDetailSidePanel item={selected} authFetch={authFetch} onClose={() => setSelectedId(null)} />
        )}
      </div>
    </>
  );
}
